import React, { useState } from 'react';
import { Heart, ExternalLink, Sparkles, ArrowRight, Loader2, CheckCircle2, BookmarkX } from 'lucide-react';
import { useAuth } from '../AuthProvider';
import { Card } from './ui/Card';
import { getAuthHeaders, customFetch } from '../services/apiService';
import { useFavoriteApps, useToggleFavoriteMutation } from '../hooks/useQueries';

export interface FavoriteAppItem {
  id: string;
  name: string;
  description?: string;
  category?: string;
  tag?: string;
  icon?: string;
  thumbnailUrl?: string;
  link?: string;
  url?: string;
  usageCount?: number;
}

export const FavoriteAppsSection: React.FC = () => {
  const { userProfile, setUserProfile } = useAuth();
  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const [processingFavId, setProcessingFavId] = useState<string | null>(null);

  // 1. DATA FETCHING VỚI REACT QUERY
  const { data: favoriteApps = [], isLoading: loading } = useFavoriteApps(userProfile?.favorites);
  const toggleFavoriteMutation = useToggleFavoriteMutation();

  // Hiển thị thông báo Toast nhẹ
  const showToast = (msg: string) => {
    setToastMessage(msg);
    setTimeout(() => {
      setToastMessage(null);
    }, 3000);
  };

  // 2. XỬ LÝ KHI BẤM BỎ YÊU THÍCH (UNFAVORITE)
  const handleRemoveFavorite = async (appId: string) => {
    if (processingFavId === appId) return;
    setProcessingFavId(appId);

    // Đồng bộ mảng favorites trong AuthContext
    if (userProfile) {
      const updatedFavs = (userProfile.favorites || []).filter(id => id !== appId);
      setUserProfile(prev => prev ? { ...prev, favorites: updatedFavs } : null);
    }

    showToast("Đã xóa khỏi danh sách yêu thích");

    try {
      await toggleFavoriteMutation.mutateAsync({ appId, action: 'remove' });
    } catch (err) {
      console.warn('Lỗi khi gọi API Bỏ yêu thích:', err);
    } finally {
      setProcessingFavId(null);
    }
  };

  // 3. XỬ LÝ KHI BẤM NÚT SỬ DỤNG
  const handleUseApp = (app: FavoriteAppItem) => {
    const rootDomain = window.location.hostname.split('.').slice(-2).join('.');
    const defaultUrl = (rootDomain === 'localhost' || rootDomain.includes('127.0.0.1'))
      ? `http://${app.id}.localhost:3000`
      : `https://${app.id}.${rootDomain}`;
    const targetUrl = app.link || app.url || defaultUrl;
    
    // Mở liên kết trong tab mới
    window.open(targetUrl, '_blank', 'noopener,noreferrer');

    // Gọi ngầm API tăng lượt sử dụng
    customFetch(`/api/apps/${encodeURIComponent(app.id)}/use`, {
      method: 'POST',
      headers: getAuthHeaders(),
      credentials: 'include'
    }).catch(err => console.warn('Lỗi gọi API use app:', err));
  };

  return (
    <Card title="Ứng dụng yêu thích">
      {/* Toast Notification */}
      {toastMessage && (
        <div className="mb-4 p-3 bg-emerald-50 border border-emerald-200 text-emerald-800 rounded-xl text-xs font-semibold flex items-center space-x-2 animate-fade-in shadow-xs">
          <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
          <span>{toastMessage}</span>
        </div>
      )}

      {/* A. KHI ĐANG TẢI (LOADING SKELETON) */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1, 2, 3].map((item) => (
            <div key={item} className="p-4 border border-slate-200 rounded-2xl bg-white space-y-3 animate-pulse">
              <div className="flex items-center justify-between">
                <div className="w-10 h-10 bg-slate-200 rounded-xl"></div>
                <div className="w-16 h-5 bg-slate-100 rounded-full"></div>
              </div>
              <div className="h-4 bg-slate-200 rounded w-3/4"></div>
              <div className="h-3 bg-slate-100 rounded w-full"></div>
              <div className="h-3 bg-slate-100 rounded w-2/3"></div>
              <div className="flex items-center justify-between pt-2">
                <div className="h-8 bg-purple-100 rounded-lg w-24"></div>
                <div className="w-8 h-8 bg-rose-100 rounded-full"></div>
              </div>
            </div>
          ))}
        </div>
      ) : favoriteApps.length === 0 ? (
        /* B. KHI CHƯA CÓ ỨNG DỤNG YÊU THÍCH (EMPTY STATE) */
        <div className="p-8 md:p-12 text-center border-2 border-dashed border-slate-200 rounded-2xl bg-slate-50/50 flex flex-col items-center justify-center space-y-4">
          <div className="w-16 h-16 rounded-full bg-rose-50 text-rose-400 flex items-center justify-center border border-rose-100 shadow-2xs">
            <BookmarkX className="w-8 h-8 stroke-[1.5]" />
          </div>
          <div className="space-y-1">
            <h3 className="text-base font-bold text-slate-800">
              Bạn chưa lưu ứng dụng AI yêu thích nào.
            </h3>
            <p className="text-xs text-slate-500 max-w-sm mx-auto">
              Lưu các trợ lý AI và công cụ giảng dạy thường dùng để truy cập nhanh chóng mọi lúc.
            </p>
          </div>
          <a
            href={`https://${window.location.hostname.split('.').slice(-2).join('.')}/kho-ung-dung`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center space-x-2 px-4 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-semibold rounded-xl shadow-sm transition-all hover:shadow-md active:scale-98"
          >
            <span>Khám phá Kho ứng dụng</span>
            <ArrowRight className="w-4 h-4" />
          </a>
        </div>
      ) : (
        /* C. KHI ĐÃ CÓ ỨNG DỤNG YÊU THÍCH (GRID RESPONSIVE) */
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {favoriteApps.map((app) => (
            <div
              key={app.id}
              className="p-4 border border-slate-200 hover:border-purple-200 rounded-2xl bg-white shadow-2xs hover:shadow-md transition-all flex flex-col justify-between group relative"
            >
              <div>
                {/* Header Card: Icon & Badge */}
                <div className="flex items-start justify-between mb-3">
                  <div className="w-10 h-10 rounded-xl bg-purple-50 border border-purple-100 flex items-center justify-center overflow-hidden shrink-0 shadow-2xs">
                    {app.thumbnailUrl || app.icon ? (
                      <img
                        src={app.thumbnailUrl || app.icon}
                        alt={app.name}
                        referrerPolicy="no-referrer"
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          // Fallback icon nếu ảnh hỏng
                          (e.currentTarget as HTMLElement).style.display = 'none';
                          const parent = (e.currentTarget as HTMLElement).parentElement;
                          if (parent && !parent.querySelector('.fallback-icon')) {
                            const fallback = document.createElement('div');
                            fallback.className = 'fallback-icon text-purple-600 font-bold text-xs flex items-center justify-center w-full h-full';
                            fallback.innerText = app.name ? app.name.charAt(0) : 'AI';
                            parent.appendChild(fallback);
                          }
                        }}
                      />
                    ) : (
                      <Sparkles className="w-5 h-5 text-purple-600" />
                    )}
                  </div>

                  {(app.category || app.tag) && (
                    <span className="text-[11px] font-bold px-2.5 py-0.5 bg-purple-50 text-purple-700 border border-purple-100 rounded-full shrink-0">
                      {app.category || app.tag}
                    </span>
                  )}
                </div>

                {/* Body Card: Tên & Mô tả */}
                <h4 className="text-sm font-bold text-slate-900 group-hover:text-purple-700 transition-colors line-clamp-1 mb-1">
                  {app.name}
                </h4>
                <p className="text-xs text-slate-500 line-clamp-2 leading-relaxed mb-4">
                  {app.description || 'Ứng dụng hỗ trợ giảng dạy và quản lý trong hệ sinh thái Nhà Giáo Số.'}
                </p>
              </div>

              {/* Footer Card: Nút "Sử dụng" & Nút "Trái tim" */}
              <div className="flex items-center justify-between pt-2 border-t border-slate-100 mt-2">
                <button
                  type="button"
                  onClick={() => handleUseApp(app)}
                  className="inline-flex items-center space-x-1.5 px-3.5 py-2 bg-purple-600 hover:bg-purple-700 text-white text-xs font-semibold rounded-xl shadow-xs transition-colors active:scale-98"
                >
                  <span>Sử dụng</span>
                  <ExternalLink className="w-3.5 h-3.5" />
                </button>

                <button
                  type="button"
                  title="Bỏ yêu thích"
                  onClick={() => handleRemoveFavorite(app.id)}
                  disabled={processingFavId === app.id}
                  className="p-2 rounded-xl text-rose-500 hover:bg-rose-50 transition-colors border border-transparent hover:border-rose-100 active:scale-95 disabled:opacity-50"
                >
                  <Heart className="w-5 h-5 fill-rose-500 text-rose-500" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
};

export default FavoriteAppsSection;
